import { PitchType, PitchTypeWithChargeType } from './types'
import { Party } from '../campsites/types'
import { validatePitchtypeCapacity } from '~/utility/campsite/party-validation'

function isPitchtypeMatchingTypes(pitchtype: PitchType, types: number[]) {
  const pitchtypeCategoryIds = pitchtype.categories.map((c) =>
    parseInt(c.id, 10),
  )
  return types.length === 0
    ? true
    : pitchtypeCategoryIds.some((id) => types.includes(id))
}

export function getPitchtypesMatchingTypes(
  pitchtypes: PitchType[] | PitchTypeWithChargeType[],
  types: number[],
  facets: string[],
) {
  return pitchtypes
    .filter((pitchtype) => isPitchtypeMatchingTypes(pitchtype, types))
    .filter((p) => {
      if (facets.length === 0) {
        return true
      }
      if (typeof p.facetKeywords === 'undefined') {
        return true
      }
      // Pitch type must match ALL selected facets, not just some
      return facets.every((f) => p.facetKeywords!.includes(f))
    })
}

export function getPitchtypesNotMatchingTypes(
  pitchtypes: PitchType[] | PitchTypeWithChargeType[],
  types: number[],
  facets: string[],
) {
  const matchingPitchtypes = getPitchtypesMatchingTypes(
    pitchtypes,
    types,
    facets,
  )
  return pitchtypes.filter(
    (pitchtype) => !matchingPitchtypes.includes(pitchtype),
  )
}

export function getAvailablePitchtypesMatchingTypes(
  pitchtypes: PitchTypeWithChargeType[],
  types: number[],
) {
  const matchingPitchtypes = getPitchtypesMatchingTypes(pitchtypes, types, [])
  return matchingPitchtypes.filter(
    (p) => (p as PitchTypeWithChargeType).chargetype.state === 'available',
  )
}

function isPitchtypeWithinCapacity(
  pitchtype: PitchType,
  party: Party | null,
): boolean {
  if (!party) {
    return true
  }

  // Convert Party to SearchParty format for validation
  const searchParty = {
    adults: party.adults,
    childAges: party.childAges,
    dogs: party.dogs || 0,
  }

  const validation = validatePitchtypeCapacity(searchParty, pitchtype)
  return !validation.isOverCapacity
}

export function getPitchtypesMatchingTypesAndCapacity(
  pitchtypes: PitchType[] | PitchTypeWithChargeType[],
  types: number[],
  facets: string[],
  party: Party | null,
) {
  return pitchtypes
    .filter((pitchtype) => isPitchtypeMatchingTypes(pitchtype, types))
    .filter((p) => {
      if (facets.length === 0) {
        return true
      }
      // Pitch types without facetKeywords don't match when facets are selected
      // This ensures they appear in "Other options" section
      if (typeof p.facetKeywords === 'undefined') {
        return false
      }
      // Pitch type must match ALL selected facets, not just some
      return facets.every((f) => p.facetKeywords!.includes(f))
    })
    .filter((pitchtype) => isPitchtypeWithinCapacity(pitchtype, party))
}

export function getPitchtypesNotMatchingTypesOrCapacity(
  pitchtypes: PitchType[] | PitchTypeWithChargeType[],
  types: number[],
  facets: string[],
  party: Party | null,
) {
  const matchingPitchtypes = getPitchtypesMatchingTypesAndCapacity(
    pitchtypes,
    types,
    facets,
    party,
  )
  return pitchtypes.filter(
    (pitchtype) => !matchingPitchtypes.includes(pitchtype),
  )
}
